"use client";

import { Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { shareContent } from "@/lib/share";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ShareButtonProps {
  title: string;
  text?: string;
  url?: string;
  variant?: "outline" | "ghost" | "default" | "secondary";
  size?: "sm" | "icon" | "default" | "lg";
  iconOnly?: boolean;
  className?: string;
  /** Optional callback invoked after a successful share (use for analytics). */
  onShare?: () => void;
}

export function ShareButton({ title, text, url, variant = "outline", size = "sm", iconOnly = false, className, onShare }: ShareButtonProps) {
  const { toast } = useToast();
  const t = useTranslations("common");

  const handleShare = async () => {
    // In the app this opens the share panel, which shows its own feedback;
    // the toast is only for the plain fallback (no panel mounted).
    const panel = typeof window !== "undefined" && !!window.__bbShareSheet;
    const success = await shareContent({ title, text, url });
    if (success) {
      if (!panel) toast({ title: t("qrLinkCopied"), description: t("shareLinkCopiedBody") });
      onShare?.();
    }
  };

  // High-contrast outline on EVERY share button (Gordon 2026-09-10) — callers
  // can still layer their own classes on top via className.
  const outline = "border-2 border-foreground/50 hover:border-foreground";

  if (iconOnly) {
    return (
      <Button variant={variant} size="icon" onClick={handleShare} className={cn(outline, className)} aria-label={t("share")}>
        <Share2 className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button variant={variant} size={size} onClick={handleShare} className={cn(outline, className)}>
      <Share2 className="h-4 w-4 mr-1" />{t("share")}
    </Button>
  );
}
